import { useDispatch, useSelector } from 'react-redux';
import { isRegulatedLocale } from '@/shared/utility';
import { useAbsoluteNavigate } from './useAbsoluteNavigate';
import { envIsCanada } from '@/constants/constants';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useCallback } from 'react';
import { setRegistrationOpenedFrom } from '@/store/ui/actions';
import {
  HomeHeroAuthenticationStep,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import useIsHomePage from '@/hooks/useIsHomePage';
import { scrollToTop } from '@/utils/utils';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

/*
 Central hook for defining the action when a player clicks on a register button anywhere in the app.
 All register buttons should call this hook, and avoid in-line logic.

 In restricted mode registration is removed entirely (see selectIsRestrictedMode — currently the
 whole Thailand market). openRegistration becomes a no-op and isRegistrationRemoved lets every
 register CTA hide itself so no registration surface is reachable.
 */
export function useRegistration() {
  const locale = useSelector(selectLocale);
  const dispatch = useDispatch();
  const absoluteNavigate = useAbsoluteNavigate();
  const isHomePage = useIsHomePage();
  const { setHomeHeroAuthenticationStep } = useHomeHeroAuthenticationStepActions();
  const { openModal, closeModal } = useModal();
  const isRegistrationRemoved = useRestrictedMode();

  const openRegistration = useCallback(
    (params: { openedFrom: OpenedFrom }): void => {
      // Registration is removed in restricted mode — swallow every trigger (buttons, CMS CTAs,
      // ?modal=register deep-link, login "switch to register" links).
      if (isRegistrationRemoved) {
        return;
      }

      // we set this OpenedFrom in the store to be able to track where the user started the Registration intent
      // so that we might bring him back to where he started upon registration success
      dispatch(setRegistrationOpenedFrom(params.openedFrom));
      triggerGTag(GTagEvents.open_registration, {
        openedFrom: params.openedFrom,
      });

      // SWEDEN & FINLAND
      if (isRegulatedLocale(locale)) {
        openModal(MODALS.PAYNPLAY_IFRAME);
        return;
      }

      // CANADA BUILD
      if (envIsCanada()) {
        setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.Registration);
        openModal(MODALS.REGISTER_MANAGER, { noBackdrop: true, pushHistory: true });
        return;
      }

      // NORMAL BUILD & MEXICO
      setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.Registration);
      if (!isHomePage) absoluteNavigate('/');
      scrollToTop();
      closeModal();
    },
    [isHomePage, locale, isRegistrationRemoved],
  );

  return { openRegistration, isRegistrationRemoved };
}
